import { Ionicons } from '@expo/vector-icons';
import React, { useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { useThemePalette } from '@/config/theme';
import { useScores365Bracket } from '@/features/competition/hooks';
import type { BracketGame, BracketGameSide } from '@/lib/data/provider';
import { formatKickoffTime, formatMatchDate, formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

// stageNum 1 = group stage; the knockout proper is anything beyond it.
const KNOCKOUT_MIN_STAGE = 2;
const COLUMN_WIDTH = 232;

/**
 * The knockout rounds of a tournament, deepest-last. `id` keys the column,
 * `titleKey` names it (bilingual), and `slots` is how many ties the round holds
 * in a complete bracket — used to pad the round with graceful TBD placeholders
 * before 365 has drawn every tie, so the WHOLE road to the final is visible from
 * the very first knockout game rather than one lonely card.
 */
interface RoundSpec {
  id: string;
  titleKey: string;
  slots: number;
}

/**
 * 365 numbers every game in a competition globally on `groupName`
 * ("Match {N} - {Venue}"). For a modern 48-team World Cup the group stage is
 * matches 1–72 and the knockout is a fixed tree:
 *   Round of 32  → matches 73–88  (16 ties)
 *   Round of 16  → matches 89–96  (8 ties)
 *   Quarter-finals → 97–100       (4 ties)
 *   Semi-finals  → 101–102        (2 ties)
 *   Third place  → 103            (1 tie)
 *   Final        → 104            (1 tie)
 * This lets us derive a tie's round from its match number even though 365 labels
 * every knockout `roundName` generically as "Round" and reports a uniform
 * `stageNum` of 2 — so we can render the full bracket structure (with TBD ties
 * for the rounds not yet drawn) the moment the knockout begins.
 */
const WC48_ROUNDS: RoundSpec[] = [
  { id: 'r32', titleKey: 'leagues.stageLast32', slots: 16 },
  { id: 'r16', titleKey: 'leagues.stageLast16', slots: 8 },
  { id: 'qf', titleKey: 'leagues.stageQuarter', slots: 4 },
  { id: 'sf', titleKey: 'leagues.stageSemi', slots: 2 },
  { id: 'third', titleKey: 'leagues.stageThird', slots: 1 },
  { id: 'final', titleKey: 'leagues.stageFinal', slots: 1 },
];
const WC48_GROUP_MATCHES = 72;
/** Inclusive match-number ranges that bucket a knockout tie into a WC48 round. */
const WC48_RANGES: { id: string; from: number; to: number }[] = [
  { id: 'r32', from: 73, to: 88 },
  { id: 'r16', from: 89, to: 96 },
  { id: 'qf', from: 97, to: 100 },
  { id: 'sf', from: 101, to: 102 },
  { id: 'third', from: 103, to: 103 },
  { id: 'final', from: 104, to: 104 },
];

/** Pull the global match number out of a 365 `groupName` ("Match 73 - …"). */
function matchNumberOf(game: BracketGame): number | null {
  const match = /match\s+(\d+)/i.exec(game.groupName ?? '');
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
}

/** Pull the venue/city out of a 365 `groupName` ("Match 73 - Inglewood" → "Inglewood"). */
function venueOf(game: BracketGame): string | null {
  const match = /match\s+\d+\s*[-–—]\s*(.+)$/i.exec(game.groupName ?? '');
  const venue = match?.[1]?.trim();
  return venue ? venue : null;
}

/** A populated round column: its spec plus the real ties that have been drawn. */
interface BuiltRound {
  spec: RoundSpec;
  games: BracketGame[];
}

/**
 * Is this a 48-team World Cup we can safely scaffold? We require POSITIVE
 * evidence — a knockout tie whose match number lands in the WC48 knockout range
 * (≥73), OR a full 12-group field — before drawing TBD placeholders, so we never
 * invent a road-to-final that doesn't match the real competition shape.
 */
function looksLikeWc48(games: BracketGame[]): boolean {
  const groups = new Set<string>();
  for (const game of games) {
    const name = game.groupName ?? '';
    if (/^group\s/i.test(name)) groups.add(name.toLowerCase());
    const matchNo = matchNumberOf(game);
    if (matchNo != null && matchNo > WC48_GROUP_MATCHES) return true;
  }
  return groups.size >= 12;
}

/**
 * Build the ordered knockout columns. When the field is a recognisable 48-team
 * World Cup we lay out the full fixed tree (R32 → Final) and slot each real tie
 * into its round by match number; rounds 365 hasn't drawn yet stay empty and get
 * padded with TBD placeholders by the column renderer. When the shape is NOT
 * confidently a WC48 (a different competition, or pre-knockout) we return null so
 * the caller renders the known ties richly without inventing structure.
 */
function buildWc48Rounds(games: BracketGame[]): BuiltRound[] | null {
  if (!looksLikeWc48(games)) return null;
  const byRound = new Map<string, BracketGame[]>(WC48_ROUNDS.map((spec) => [spec.id, []]));
  for (const game of games) {
    const matchNo = matchNumberOf(game);
    if (matchNo == null) continue;
    const range = WC48_RANGES.find((r) => matchNo >= r.from && matchNo <= r.to);
    if (range) byRound.get(range.id)?.push(game);
  }
  return WC48_ROUNDS.map((spec) => ({
    spec,
    games: (byRound.get(spec.id) ?? []).sort(byStartTime),
  }));
}

/**
 * Fallback grouping for any non-WC48 knockout (or partial data we can't scaffold
 * safely): bucket by `stageNum` and name each round by its DISTANCE from the
 * deepest stage present — the deepest is the Final, one back the Semis, etc. No
 * TBD padding here because we can't know a round's true slot count without the
 * fixed WC48 shape. Columns order left→right by stageNum ascending.
 */
const DISTANCE_TITLES = [
  'leagues.stageFinal',
  'leagues.stageSemi',
  'leagues.stageQuarter',
  'leagues.stageLast16',
  'leagues.stageLast32',
  'leagues.stageLast64',
];
function buildGenericRounds(games: BracketGame[]): BuiltRound[] {
  const stages = new Map<number, BracketGame[]>();
  for (const game of games) {
    const stage = game.stageNum ?? KNOCKOUT_MIN_STAGE;
    if (!stages.has(stage)) stages.set(stage, []);
    stages.get(stage)?.push(game);
  }
  const ordered = [...stages.keys()].sort((a, b) => a - b);
  const maxStage = ordered[ordered.length - 1] ?? KNOCKOUT_MIN_STAGE;
  return ordered.map((stage) => {
    const distance = maxStage - stage;
    return {
      spec: {
        id: `stage-${stage}`,
        titleKey: DISTANCE_TITLES[distance] ?? 'leagues.stageRound',
        slots: 0, // unknown → no TBD padding
      },
      games: (stages.get(stage) ?? []).sort(byStartTime),
    };
  });
}

function byStartTime(a: BracketGame, b: BracketGame): number {
  const ta = a.startTime ? Date.parse(a.startTime) : Number.POSITIVE_INFINITY;
  const tb = b.startTime ? Date.parse(b.startTime) : Number.POSITIVE_INFINITY;
  return ta - tb;
}

type TieStatus = 'live' | 'ended' | 'scheduled';
function statusOf(game: BracketGame): TieStatus {
  const text = (game.status ?? '').toLowerCase();
  if (/end|ft|full|finished/.test(text)) return 'ended';
  if (/live|half|min|'|progress|\d+'/.test(text)) return 'live';
  return 'scheduled';
}

/** One side of a tie: crest + name + score, dimmed when the side has lost. */
function TieSide({
  side,
  score,
  isWinner,
  decided,
  showScore,
}: {
  side: BracketGameSide;
  score: number | null;
  isWinner: boolean;
  decided: boolean;
  showScore: boolean;
}) {
  const { numeralOptions, locale } = useLocale();
  const name =
    locale === 'ar' ? side.name ?? '' : side.name ?? side.symbolicName ?? '';
  const dim = decided && !isWinner;
  return (
    <View className="flex-row items-center gap-2.5">
      <TeamCrest uri={side.crest} name={name || '?'} size="sm" />
      <AppText
        variant="caption"
        weight={isWinner && decided ? 'bold' : 'medium'}
        className={`min-w-0 shrink ${dim ? 'text-muted' : 'text-ink'}`}
        numberOfLines={1}
      >
        {name || '—'}
      </AppText>
      {showScore ? (
        <AppText
          variant="caption"
          weight="bold"
          tabular
          className={`ms-auto ${dim ? 'text-muted' : isWinner ? 'text-volt-bright' : 'text-ink'}`}
        >
          {score != null && score >= 0 ? formatNumber(score, numeralOptions) : '–'}
        </AppText>
      ) : null}
    </View>
  );
}

/** A real, drawn tie: both teams, score-or-kickoff, status pill, venue + date. */
function TieCard({ game }: { game: BracketGame }) {
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const palette = useThemePalette();
  const status = statusOf(game);
  const hs = game.home.score;
  const as = game.away.score;
  const played = status !== 'scheduled';
  const decided =
    played && hs != null && hs >= 0 && as != null && as >= 0 && hs !== as;
  const homeWon = decided && (hs as number) > (as as number);
  const awayWon = decided && (as as number) > (hs as number);

  const kickoff = game.startTime ? new Date(game.startTime) : null;
  const validKickoff = kickoff && !Number.isNaN(kickoff.getTime()) ? kickoff : null;
  const venue = venueOf(game);

  return (
    <Card className="overflow-hidden px-3 py-3">
      <View className="mb-2 flex-row items-center justify-between gap-2">
        <StatusPill status={status} />
        {validKickoff ? (
          <AppText variant="micro" weight="medium" className="shrink-0 text-soft" numberOfLines={1}>
            {played
              ? formatMatchDate(validKickoff, numeralOptions)
              : formatKickoffTime(validKickoff, numeralOptions)}
          </AppText>
        ) : null}
      </View>

      <View className="gap-2">
        <TieSide side={game.home} score={hs} isWinner={homeWon} decided={decided} showScore={played} />
        <TieSide side={game.away} score={as} isWinner={awayWon} decided={decided} showScore={played} />
      </View>

      {venue || (!played && validKickoff) ? (
        <View className="mt-2.5 flex-row items-center gap-1.5 border-t border-line/50 pt-2">
          <Ionicons name="location-outline" size={11} color={palette.muted} />
          <AppText variant="micro" className="min-w-0 shrink text-muted" numberOfLines={1}>
            {[venue, !played && validKickoff ? formatMatchDate(validKickoff, numeralOptions) : null]
              .filter(Boolean)
              .join(' · ') || t('match.venue')}
          </AppText>
        </View>
      ) : null}
    </Card>
  );
}

/** A small live/ended/scheduled pill — the acid-green accent only on LIVE. */
function StatusPill({ status }: { status: TieStatus }) {
  const { t } = useTranslation();
  if (status === 'live') {
    return (
      <View className="flex-row items-center gap-1 rounded-full bg-volt/15 px-2 py-0.5">
        <View className="h-1.5 w-1.5 rounded-full bg-volt-bright" />
        <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-volt">
          {t('common.live')}
        </AppText>
      </View>
    );
  }
  return (
    <View className="rounded-full bg-surface-2 px-2 py-0.5">
      <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-soft">
        {status === 'ended' ? t('common.finished') : t('common.upcoming')}
      </AppText>
    </View>
  );
}

/** An undrawn tie — the winners of earlier rounds will fill it. */
function PlaceholderTie() {
  const { t } = useTranslation();
  return (
    <Card className="overflow-hidden border-dashed px-3 py-3">
      <View className="gap-2">
        <PlaceholderSide />
        <PlaceholderSide />
      </View>
      <AppText variant="micro" className="mt-2.5 border-t border-line/50 pt-2 text-inactive" numberOfLines={1}>
        {t('predictor.awaiting')}
      </AppText>
    </Card>
  );
}

function PlaceholderSide() {
  return (
    <View className="flex-row items-center gap-2.5">
      <View className="h-[22px] w-[22px] rounded-full bg-surface-2" />
      <View className="h-2.5 w-20 rounded-full bg-surface-2" />
    </View>
  );
}

/** One round column: a header (name + tie count) and its ties, padded to slots. */
function RoundColumn({ round }: { round: BuiltRound }) {
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const drawn = round.games.length;
  // Pad to the round's known slot count with TBD ties (WC48 only — generic
  // rounds carry slots=0 and so show only what's drawn).
  const placeholders = Math.max(0, round.spec.slots - drawn);
  const total = round.spec.slots > 0 ? round.spec.slots : drawn;

  return (
    <View style={{ width: COLUMN_WIDTH }} className="gap-3">
      <View className="flex-row items-center justify-between gap-2 px-0.5">
        <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
          {t(round.spec.titleKey)}
        </AppText>
        {total > 0 ? (
          <View className="rounded-full bg-surface-2 px-2 py-0.5">
            <AppText variant="micro" weight="bold" tabular className="text-soft">
              {formatNumber(total, numeralOptions)}
            </AppText>
          </View>
        ) : null}
      </View>
      <View className="gap-2.5">
        {round.games.map((game, index) => (
          <TieCard key={game.id ?? `${round.spec.id}-${index}`} game={game} />
        ))}
        {Array.from({ length: placeholders }).map((_, index) => (
          <PlaceholderTie key={`${round.spec.id}-tbd-${index}`} />
        ))}
      </View>
    </View>
  );
}

/** Graceful placeholder shown while the tournament is still in the group stage. */
function BracketPlaceholder() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  return (
    <View className="px-4">
      <Card className="items-center gap-2.5 px-5 py-9">
        <View className="h-12 w-12 items-center justify-center rounded-full bg-volt/15">
          <Ionicons name="git-network-outline" size={24} color={palette.voltBright} />
        </View>
        <AppText variant="caption" weight="bold" className="text-center text-ink">
          {t('leagues.noBracketTitle')}
        </AppText>
        <AppText variant="micro" className="max-w-[260px] text-center leading-5 text-muted">
          {t('leagues.noBracketBody')}
        </AppText>
      </Card>
    </View>
  );
}

/**
 * The real 365scores knockout bracket as a horizontally scrollable tournament
 * tree. For a 48-team World Cup the FULL road to the final is laid out from the
 * first knockout game — Round of 32 → Round of 16 → Quarter-finals →
 * Semi-finals → Third place → Final — with each real tie slotted in by its 365
 * match number and the rounds 365 hasn't drawn yet shown as graceful TBD ties,
 * so the structure is visible early instead of a single lonely card. Any other
 * knockout (or partial data we can't safely scaffold) falls back to naming
 * columns by distance-from-final and rendering only the drawn ties.
 *
 * Each tie card carries both crests + names, score or kickoff, a live/ended/
 * upcoming pill, and the venue + date. Owns its whole presentation (skeleton,
 * placeholder, scrollers) so the screen just mounts <KnockoutBracket />.
 * RTL-safe + light/dark via palette tokens; the acid-green accent stays sparing
 * (live pills, winning scores).
 */
export function KnockoutBracket() {
  const { data, isPending } = useScores365Bracket();
  const rounds = useMemo(() => {
    const games = data ?? [];
    const knockout = games.filter((game) => (game.stageNum ?? 1) >= KNOCKOUT_MIN_STAGE);
    if (knockout.length === 0) return [];
    // Prefer the fixed WC48 tree (full road-to-final with TBD padding); fall back
    // to distance-named columns for anything we can't confidently scaffold.
    return buildWc48Rounds(games) ?? buildGenericRounds(knockout);
  }, [data]);

  return (
    <ScrollView contentContainerStyle={{ paddingBottom: 48, paddingTop: 4 }}>
      {isPending ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ paddingHorizontal: 16, gap: 16 }}
        >
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-64 w-56" />
          ))}
        </ScrollView>
      ) : rounds.length === 0 ? (
        <BracketPlaceholder />
      ) : (
        <View className="gap-3">
          <BracketSubtitle />
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ paddingHorizontal: 16, gap: 14 }}
          >
            {rounds.map((round) => (
              <RoundColumn key={round.spec.id} round={round} />
            ))}
          </ScrollView>
        </View>
      )}
    </ScrollView>
  );
}

/**
 * The "road to the final" subtitle — kept INSIDE the bracket so it only ever
 * shows when there are real rounds. During the group stage the placeholder owns
 * its own copy, so the subtitle never sits above a "no bracket yet" card.
 */
function BracketSubtitle() {
  const { t } = useTranslation();
  return (
    <AppText variant="micro" className="px-4 leading-5 text-muted">
      {t('wcGroups.bracketSubtitle')}
    </AppText>
  );
}
